import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  FolderKanban,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
  LogOut,
  LogIn,
  Star,
  LayoutGrid,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/useAuthStore'
import { login, logout } from '@/api/auth'
import { UserAvatar } from '@/components/ui/avatar'
import { projectsApi } from '@/api/projects'
import { useFavorites } from '@/features/projects/hooks/useFavorites'
import { NavItem, NavItemAction } from '@/components/ui/nav-item'
import type { Project } from '@/types/project'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const { user, isLoading } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const { projectId: currentProjectId } = useParams<{ projectId: string }>()
  const sidebarRef = useRef<HTMLElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const [projectsOpen, setProjectsOpen] = useState(true)

  // Fetch all projects
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.getAll,
  })
  const { isFavorite, toggleFavorite } = useFavorites()

  // Sort projects: favorites first, then alphabetically
  const sortedProjects = [...projects].sort((a: Project, b: Project) => {
    const aFav = isFavorite(a.id)
    const bFav = isFavorite(b.id)
    if (aFav && !bFav) return -1
    if (!aFav && bFav) return 1
    return a.name.localeCompare(b.name)
  })

  // Focus management for mobile sidebar
  useEffect(() => {
    if (mobileOpen && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [mobileOpen])

  // Close user menu on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Trap focus within mobile sidebar
  useEffect(() => {
    if (!mobileOpen) return

    const sidebar = sidebarRef.current
    if (!sidebar) return

    const focusableElements = sidebar.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [mobileOpen])
  return (
    <aside
      ref={sidebarRef}
      id="mobile-sidebar"
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        'fixed left-0 top-0 z-50 h-screen bg-surface border-r border-border transition-all duration-300 ease-in-out',
        // Desktop styles
        'hidden md:block',
        collapsed ? 'md:w-16' : 'md:w-56',
        // Mobile styles - full width overlay
        mobileOpen && 'block w-full sm:w-72'
      )}
    >
      <div className="flex h-full flex-col py-4">
        {/* Logo and Toggle */}
        <div className={cn(
          'relative flex items-center mb-6 px-3',
          collapsed && !mobileOpen ? 'justify-center' : 'justify-between'
        )}>
          <Link
            to="/projects"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg"
            aria-label="Go to project registry"
          >
            <div className="w-10 h-10 rounded-lg bg-monokai-pink flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-lg font-mono" aria-hidden="true">D</span>
            </div>
            {(!collapsed || mobileOpen) && (
              <span className="text-lg font-semibold whitespace-nowrap">DSP</span>
            )}
          </Link>

          {/* Mobile Close Button */}
          {mobileOpen && (
            <button
              ref={closeButtonRef}
              onClick={onMobileClose}
              className="flex md:hidden h-10 w-10 min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Close navigation menu"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          )}

          {/* Desktop Toggle Button - consistent position */}
          {!mobileOpen && (
            <button
              onClick={onToggle}
              className={cn(
                'hidden md:flex h-10 w-10 min-h-[44px] min-w-[44px] items-center justify-center rounded-md',
                'text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                collapsed && 'absolute right-3'
              )}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className={cn(
          'flex flex-1 flex-col gap-1 overflow-hidden',
          collapsed && !mobileOpen ? 'items-center px-2' : 'px-3'
        )}>
          {/* Projects Section */}
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Projects Header */}
            {(!collapsed || mobileOpen) ? (
              <NavItem
                icon={<FolderKanban className="h-4 w-4" />}
                label="Projects"
                foldable
                isOpen={projectsOpen}
                onToggle={() => setProjectsOpen(!projectsOpen)}
                count={sortedProjects.length}
              />
            ) : (
              <Link
                to="/projects"
                className={cn(
                  'flex items-center justify-center h-10 w-10 min-h-[44px] min-w-[44px] rounded-lg transition-colors mb-1',
                  'text-muted-foreground hover:bg-secondary hover:text-accent',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                )}
                title="Projects"
                aria-label="View projects"
              >
                <LayoutGrid className="h-5 w-5" aria-hidden="true" />
              </Link>
            )}

            {/* Project List */}
            {projectsOpen && (!collapsed || mobileOpen) && (
              <div className="flex-1 overflow-y-auto space-y-0.5 pr-1 ml-2">
                {sortedProjects.map((project: Project) => {
                  const isActive = currentProjectId === project.id || location.pathname === `/projects/${project.id}`
                  const isFav = isFavorite(project.id)

                  return (
                    <NavItem
                      key={project.id}
                      icon={<FolderKanban className="h-3.5 w-3.5" />}
                      label={project.name}
                      selected={isActive}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      actions={
                        <NavItemAction
                          icon={<Star className={cn('h-3 w-3', isFav && 'fill-current text-yellow-500')} />}
                          title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                          onClick={() => toggleFavorite(project.id)}
                          className={isFav ? 'opacity-100 text-yellow-500' : ''}
                        />
                      }
                    />
                  )
                })}

                {sortedProjects.length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    No projects yet
                  </div>
                )}
              </div>
            )}

            {/* Collapsed: Show project icons */}
            {collapsed && !mobileOpen && (
              <div className="flex-1 overflow-y-auto space-y-1" role="list" aria-label="Projects">
                {sortedProjects.slice(0, 6).map((project: Project) => {
                  const isActive = currentProjectId === project.id
                  const isFav = isFavorite(project.id)

                  return (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className={cn(
                        'flex items-center justify-center h-10 w-10 min-h-[44px] min-w-[44px] rounded-lg transition-colors relative',
                        'hover:bg-secondary hover:text-accent',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        isActive ? 'bg-secondary text-accent' : 'text-muted-foreground'
                      )}
                      title={project.name}
                      aria-label={`${project.name}${isFav ? ' (favorite)' : ''}${isActive ? ' (current)' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      role="listitem"
                    >
                      <FolderKanban className="h-5 w-5" aria-hidden="true" />
                      {isFav && (
                        <Star className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 text-yellow-500 fill-current" aria-hidden="true" />
                      )}
                    </Link>
                  )
                })}
                {sortedProjects.length > 6 && (
                  <Link
                    to="/projects"
                    className={cn(
                      'flex items-center justify-center h-10 w-10 min-h-[44px] min-w-[44px] rounded-lg transition-colors',
                      'text-muted-foreground hover:bg-secondary hover:text-accent',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    )}
                    title={`+${sortedProjects.length - 6} more projects`}
                    aria-label={`View all ${sortedProjects.length} projects`}
                  >
                    <span className="text-xs font-medium">+{sortedProjects.length - 6}</span>
                  </Link>
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Bottom Section: User + Settings */}
        <div className={cn('mt-auto space-y-1', collapsed && !mobileOpen ? 'px-2' : 'px-3')}>
          {/* Divider */}
          <div className="border-t border-border my-2" />

          {/* User Section */}
          {isLoading ? (
            <div className={cn(
              'flex items-center rounded-lg min-h-[44px]',
              collapsed && !mobileOpen ? 'h-10 w-10 justify-center' : 'h-12 md:h-10 px-3 gap-3'
            )}>
              <div className="h-8 w-8 rounded-full bg-secondary animate-pulse shrink-0" />
              {(!collapsed || mobileOpen) && (
                <div className="flex-1 space-y-1">
                  <div className="h-3 w-20 bg-secondary animate-pulse rounded" />
                  <div className="h-2 w-24 bg-secondary animate-pulse rounded" />
                </div>
              )}
            </div>
          ) : user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={cn(
                  'flex items-center rounded-lg transition-colors min-h-[44px] w-full',
                  collapsed && !mobileOpen ? 'h-10 w-10 justify-center' : 'h-12 md:h-10 px-3 gap-3',
                  'hover:bg-secondary',
                  userMenuOpen && 'bg-secondary'
                )}
                aria-label={`User menu for ${user.email}`}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                <UserAvatar
                  avatarUrl={user.avatarUrl}
                  email={user.email}
                  name={user.name || user.authentikUsername}
                  size={32}
                  className="shrink-0"
                />
                {(!collapsed || mobileOpen) && (
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user.name || user.authentikUsername || user.email.split('@')[0]}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                )}
              </button>

              {/* User Menu Popup */}
              {userMenuOpen && (
                <div
                  className={cn(
                    'absolute bottom-full mb-2 rounded-lg border border-border bg-surface shadow-popover py-1 z-50',
                    collapsed && !mobileOpen ? 'left-0 w-48' : 'left-0 right-0'
                  )}
                  role="menu"
                  aria-orientation="vertical"
                >
                  {(collapsed && !mobileOpen) && (
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-sm font-medium truncate">
                        {user.name || user.authentikUsername || user.email.split('@')[0]}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  )}
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 min-h-[44px] text-sm text-foreground hover:bg-secondary transition-colors"
                    role="menuitem"
                  >
                    <Settings className="w-4 h-4" aria-hidden="true" />
                    Settings
                  </Link>
                  <button
                    onClick={() => logout()}
                    className="flex w-full items-center gap-2 px-3 py-2 min-h-[44px] text-sm text-foreground hover:bg-secondary transition-colors"
                    role="menuitem"
                  >
                    <LogOut className="w-4 h-4" aria-hidden="true" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => login(location.pathname)}
              className={cn(
                'flex items-center rounded-lg transition-colors min-h-[44px] w-full',
                collapsed && !mobileOpen ? 'h-10 w-10 justify-center bg-monokai-pink text-white' : 'h-12 md:h-10 px-3 gap-3 bg-monokai-pink text-white',
                'hover:bg-monokai-pink/90'
              )}
              aria-label="Login to your account"
              title={collapsed && !mobileOpen ? 'Login' : undefined}
            >
              <LogIn className="h-5 w-5 shrink-0" aria-hidden="true" />
              {(!collapsed || mobileOpen) && (
                <span className="font-medium">Login</span>
              )}
            </button>
          )}
        </div>
      </div>
    </aside>
  )
}
